import type { Types } from '@cornerstonejs/core';
import {
  RenderingEngine,
  Enums,
  getRenderingEngine,
} from '@cornerstonejs/core';
import {
  initDemo,
  createImageIdsAndCacheMetaData,
  setTitleAndDescription,
  addButtonToToolbar,
  addDropdownToToolbar,
  viewportId,
  renderingEngineId,
  addUploadToToolbar,
  toolGroupId,
  imageIds,
  setImageIds,
} from '../../../../utils/demo/helpers';
import * as cornerstoneTools from '@cornerstonejs/tools';
import dicomImageLoader from '@cornerstonejs/dicom-image-loader';

// This is for debugging purposes
console.warn(
  'Click on index.ts to open source code for this example --------->'
);

const { wadors } = dicomImageLoader;

const {
  LengthTool,
  HeightTool,
  ProbeTool,
  RectangleROITool,
  EllipticalROITool,
  CircleROITool,
  BidirectionalTool,
  AngleTool,
  CobbAngleTool,
  ToolGroupManager,
  ArrowAnnotateTool,
  PlanarFreehandROITool,
  StackScrollTool,
  Enums: csToolsEnums,
  utilities,
} = cornerstoneTools;

const { ViewportType, Events } = Enums;
const { MouseBindings } = csToolsEnums;

// ======== Set up page ======== //
setTitleAndDescription(
  'Calibration Tools Stack',
  'Calibration tools for a stack viewport (aspect ratio changes only supported initially)'
);

const content = document.getElementById('content');
const element = document.createElement('div');

// Disable right click context menu so we can have right click tools
element.oncontextmenu = (e) => e.preventDefault();

element.id = 'cornerstone-element';
element.style.width = '512px';
element.style.height = '512px';

content.appendChild(element);

const info = document.createElement('div');
content.appendChild(info);

const instructions = document.createElement('p');
instructions.innerText = 'Left Click to use selected tool';
info.appendChild(instructions);

const rotationInfo = document.createElement('div');
info.appendChild(rotationInfo);

const flipHorizontalInfo = document.createElement('div');
info.appendChild(flipHorizontalInfo);

const flipVerticalInfo = document.createElement('div');
info.appendChild(flipVerticalInfo);

element.addEventListener(Events.CAMERA_MODIFIED, (_) => {
  // Get the rendering engine
  const renderingEngine = getRenderingEngine(renderingEngineId);

  // Get the stack viewport
  const viewport = <Types.IStackViewport>(
    renderingEngine.getViewport(viewportId)
  );

  if (!viewport) {
    return;
  }

  const { flipHorizontal, flipVertical } = viewport.getCamera();
  const { rotation } = viewport.getViewPresentation();

  rotationInfo.innerText = `Rotation: ${Math.round(rotation)}`;
  flipHorizontalInfo.innerText = `Flip horizontal: ${flipHorizontal}`;
  flipVerticalInfo.innerText = `Flip vertical: ${flipVertical}`;
});
// ============================= //

addUploadToToolbar();

const toolsNames = [
  LengthTool.toolName,
  HeightTool.toolName,
  ProbeTool.toolName,
  RectangleROITool.toolName,
  EllipticalROITool.toolName,
  CircleROITool.toolName,
  BidirectionalTool.toolName,
  AngleTool.toolName,
  CobbAngleTool.toolName,
  ArrowAnnotateTool.toolName,
  PlanarFreehandROITool.toolName,
];
let selectedToolName = toolsNames[0];

addDropdownToToolbar({
  options: { values: toolsNames, defaultValue: selectedToolName },
  onSelectedValueChange: (newSelectedToolNameAsStringOrNumber) => {
    const newSelectedToolName = String(newSelectedToolNameAsStringOrNumber);
    const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

    // Set the new tool active
    toolGroup.setToolActive(newSelectedToolName, {
      bindings: [
        {
          mouseButton: MouseBindings.Primary, // Left Click
        },
      ],
    });

    // Set the old tool passive
    toolGroup.setToolPassive(selectedToolName);

    selectedToolName = <string>newSelectedToolName;
  },
});

const calibrationFunctions: Record<string, unknown> = {};
const originalSpacing = 0.976562;

const calibrations = [
  {
    value: 'Default',
    selected: 'userCalibration',
    calibration: {
      scale: 1,
      type: Enums.CalibrationTypes.NOT_APPLICABLE,
    },
  },
  {
    value: 'User Calibration 0.5',
    selected: 'userCalibration',
    calibration: {
      scale: 0.5,
      type: Enums.CalibrationTypes.USER,
    },
  },
  {
    value: 'ERMF 2',
    selected: 'userCalibration',
    calibration: {
      scale: 2,
      type: Enums.CalibrationTypes.ERMF,
    },
  },
  {
    value: 'Projected 1',
    selected: 'userCalibration',
    calibration: {
      // Bug right now in StackViewport that fails to reset
      scale: 1,
      type: Enums.CalibrationTypes.PROJECTION,
    },
  },
  {
    value: 'Error 1',
    selected: 'userCalibration',
    calibration: {
      scale: 1,
      type: Enums.CalibrationTypes.ERROR,
    },
  },
  {
    value: 'px units',
    selected: 'applyMetadata',
    metadata: {
      '00280030': null,
    },
  },
  {
    value: 'Size Aspect 1:2 (breaks existing annotations)',
    selected: 'applyMetadata',
    metadata: {
      '00280030': { Value: [0.5 * originalSpacing, originalSpacing] },
    },
  },
  {
    value: 'Size Aspect 1:1 (breaks existing annotations)',
    selected: 'applyMetadata',
    metadata: {
      '00280030': { Value: [originalSpacing, originalSpacing] },
    },
  },
];
const calibrationNames = calibrations.map((it) => it.value);

addDropdownToToolbar({
  options: { values: calibrationNames },
  onSelectedValueChange: (newCalibrationValue) => {
    const calibration = calibrations.find(
      (it) => it.value === newCalibrationValue
    );
    if (!calibration) {
      return;
    }
    const f = calibrationFunctions[calibration.selected];
    if (!f) {
      return;
    }
    f.apply(calibration);
  },
});

const aspects = ['1:1', '1:2', '2:1', '0.5:1', '1:0.5', '3:17'];
addDropdownToToolbar({
  id: 'aspect',
  options: {
    values: aspects,
    defaultValue: aspects[0],
  },
  onSelectedValueChange: (value) => {
    const aspect = (value as string).split(':').map((it) => Number(it));
    const renderingEngine = getRenderingEngine(renderingEngineId);
    const viewport = renderingEngine.getViewport(viewportId);
    viewport.setAspectRatio(aspect, { fit: true });
    viewport.render();
  },
});

addButtonToToolbar({
  title: 'Rotate Random',
  onClick: () => {
    // Get the rendering engine
    const renderingEngine = getRenderingEngine(renderingEngineId);

    // Get the stack viewport
    const viewport = renderingEngine.getViewport(viewportId);

    const rotation = Math.random() * 360;

    viewport.setViewPresentation({ rotation });

    viewport.render();
  },
});

addButtonToToolbar({
  title: 'Rotate Absolute 0',
  onClick: () => {
    // Get the rendering engine
    const renderingEngine = getRenderingEngine(renderingEngineId);

    // Get the stack viewport
    const viewport = renderingEngine.getViewport(viewportId);

    viewport.setViewPresentation({ rotation: 0 });

    viewport.render();
  },
});

addButtonToToolbar({
  title: 'Rotate Delta 30',
  onClick: () => {
    // Get the rendering engine
    const renderingEngine = getRenderingEngine(renderingEngineId);

    // Get the stack viewport
    const viewport = renderingEngine.getViewport(viewportId);

    const { rotation } = viewport.getViewPresentation();
    viewport.setViewPresentation({ rotation: rotation + 30 });

    viewport.render();
  },
});

/**
 * Runs the demo
 */
async function run() {
  // Init Cornerstone and related libraries
  await initDemo();

  // Add tools to Cornerstone3D
  cornerstoneTools.addTool(LengthTool);
  cornerstoneTools.addTool(HeightTool);
  cornerstoneTools.addTool(ProbeTool);
  cornerstoneTools.addTool(RectangleROITool);
  cornerstoneTools.addTool(EllipticalROITool);
  cornerstoneTools.addTool(CircleROITool);
  cornerstoneTools.addTool(BidirectionalTool);
  cornerstoneTools.addTool(AngleTool);
  cornerstoneTools.addTool(CobbAngleTool);
  cornerstoneTools.addTool(ArrowAnnotateTool);
  cornerstoneTools.addTool(PlanarFreehandROITool);
  cornerstoneTools.addTool(StackScrollTool);

  // Define a tool group, which defines how mouse events map to tool commands for
  // Any viewport using the group
  const toolGroup = ToolGroupManager.createToolGroup(toolGroupId);

  // Add the tools to the tool group
  toolGroup.addTool(LengthTool.toolName);
  toolGroup.addTool(HeightTool.toolName);
  toolGroup.addTool(ProbeTool.toolName);
  toolGroup.addTool(RectangleROITool.toolName);
  toolGroup.addTool(EllipticalROITool.toolName);
  toolGroup.addTool(CircleROITool.toolName);
  toolGroup.addTool(BidirectionalTool.toolName);
  toolGroup.addTool(AngleTool.toolName);
  toolGroup.addTool(CobbAngleTool.toolName);
  toolGroup.addTool(ArrowAnnotateTool.toolName);
  toolGroup.addTool(PlanarFreehandROITool.toolName);
  toolGroup.addTool(StackScrollTool.toolName);

  // Set the initial state of the tools, here we set one tool active on left click.
  // This means left click will draw that tool.
  toolGroup.setToolActive(LengthTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Primary, // Left Click
      },
    ],
  });
  toolGroup.setToolActive(StackScrollTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Wheel,
      },
      {
        mouseButton: MouseBindings.Auxiliary,
      },
    ],
  });

  // We set all the other tools passive here, this means that any state is rendered, and editable
  // But aren't actively being drawn (see the toolModes example for information)
  toolGroup.setToolPassive(HeightTool.toolName);
  toolGroup.setToolPassive(ProbeTool.toolName);
  toolGroup.setToolPassive(RectangleROITool.toolName);
  toolGroup.setToolPassive(EllipticalROITool.toolName);
  toolGroup.setToolPassive(CircleROITool.toolName);
  toolGroup.setToolPassive(BidirectionalTool.toolName);
  toolGroup.setToolPassive(AngleTool.toolName);
  toolGroup.setToolPassive(CobbAngleTool.toolName);
  toolGroup.setToolPassive(ArrowAnnotateTool.toolName);

  // Get Cornerstone imageIds and fetch metadata into RAM
  setImageIds(
    await createImageIdsAndCacheMetaData({
      StudyInstanceUID:
        '1.3.6.1.4.1.14519.5.2.1.7009.2403.334240657131972136850343327463',
      SeriesInstanceUID:
        '1.3.6.1.4.1.14519.5.2.1.7009.2403.226151125820845824875394858561',
      wadoRsRoot: 'https://d14fa38qiwhyfd.cloudfront.net/dicomweb',
    })
  );
  // Instantiate a rendering engine
  const renderingEngine = new RenderingEngine(renderingEngineId);

  calibrationFunctions.userCalibration = function calibrationSelected() {
    imageIds.forEach((imageId) =>
      utilities.calibrateImageSpacing(
        imageId,
        renderingEngine,
        this.calibration
      )
    );
  };
  calibrationFunctions.applyMetadata = function applyMetadata() {
    imageIds.forEach((imageId) => {
      const instance = wadors.metaDataManager.get(imageId);
      if (!instance) {
        console.warn('Can only apply image id update to metadata managed data');
      }
      Object.assign(instance, this.metadata);
      utilities.calibrateImageSpacing(imageId, renderingEngine, null);
    });
  };

  // Create a stack viewport
  const viewportInput = {
    viewportId,
    type: ViewportType.STACK,
    element,
    defaultOptions: {
      background: <Types.Point3>[0.2, 0, 0.2],
    },
  };

  renderingEngine.enableElement(viewportInput);

  // Set the tool group on the viewport
  toolGroup.addViewport(viewportId, renderingEngineId);

  // Get the stack viewport that was created
  const viewport = <Types.IStackViewport>(
    renderingEngine.getViewport(viewportId)
  );

  // Define a stack containing a single image
  const stack = [imageIds[0]];

  // Set the stack on the viewport
  viewport.setStack(stack);

  // Render the image
  viewport.render();
}

run();
